/**
 * Pure draft↔persisted mappers for the Workflows v2 editor.
 *
 * These convert between the editor's flat {@link WorkflowDraft} view-model
 * (`schedule{cadence,time,timezone}` / `condition{column,op,value:string}`) and the persisted
 * vocabulary (`WorkflowTrigger` / `WorkflowGraph` with `ConditionConfig{lhs,op,rhs:number}`).
 *
 * They live here — not inside a hook — so the load path (useWorkflow), the dry-run path
 * (useDryRun) and the agent-patch reducer (draft-reducer, T-FE2) all import ONE bijection.
 * No React / hook dependencies: keep these pure so an agent patch and a server-loaded workflow
 * land in byte-identical draft state.
 *
 * The terminal destination is always a `slack` node — the query→[condition]→slack path is the
 * only persisted shape. The `agent`-as-terminal-node concept was deferred; see
 * `docs/agent-routines-future.md`.
 */
import type { WorkflowGraph, WorkflowTrigger } from '@server/types';

import type { Cadence, Condition, ConditionOp, Schedule, WorkflowDraft } from './types';

export function scheduleToTrigger(schedule: Schedule): WorkflowTrigger {
  if (schedule.cadence === 'manual') return { type: 'manual' };
  return { type: 'recurring', cron: cadenceToCron(schedule), timezone: schedule.timezone };
}

function cadenceToCron({ cadence, time }: Schedule): string {
  const [hh = '9', mm = '0'] = time.split(':');
  switch (cadence) {
    case 'hourly':
      return '0 * * * *';
    case 'daily':
      return `${mm} ${hh} * * *`;
    case 'weekdays':
      return `${mm} ${hh} * * 1-5`;
    case 'weekly':
      return `${mm} ${hh} * * 1`;
    case 'monthly':
      return `${mm} ${hh} 1 * *`;
    case 'manual':
    default:
      return `${mm} ${hh} * * *`;
  }
}

export function triggerToSchedule(trigger: { type: string; cron?: string; timezone?: string }): Schedule {
  if (trigger.type === 'manual') {
    return { cadence: 'manual', time: '09:00', timezone: 'UTC' };
  }
  const cron = trigger.cron ?? '0 9 * * *';
  const tz = trigger.timezone ?? 'UTC';
  return { cadence: cronToCadence(cron), time: cronToTime(cron), timezone: tz };
}

function cronToCadence(cron: string): Cadence {
  // Match known patterns produced by cadenceToCron().
  if (cron === '0 * * * *') return 'hourly';
  if (/^\d+ \d+ \* \* 1-5$/.test(cron)) return 'weekdays';
  if (/^\d+ \d+ \* \* 1$/.test(cron)) return 'weekly';
  if (/^\d+ \d+ 1 \* \*$/.test(cron)) return 'monthly';
  return 'daily';
}

function cronToTime(cron: string): string {
  // Extract HH:mm from a 5-field cron ("mm hh ...")
  const parts = cron.split(' ');
  const mm = parts[0] ?? '0';
  const hh = parts[1] ?? '9';
  if (hh === '*') return '00:00';
  return `${hh.padStart(2, '0')}:${mm.padStart(2, '0')}`;
}

/** The persisted condition node config (`lhs row_count` only in v0). */
export interface ConditionConfig {
  lhs?: string;
  op?: string;
  rhs?: number;
}

/**
 * Editor condition → persisted ConditionConfig.
 *
 * An empty (or non-numeric) threshold is left OFF the config rather than coerced to `0`: the autosaved
 * working copy round-trips it back to an empty field on reload (see {@link configToCondition}), so a
 * value the user cleared isn't silently resurrected as `row_count <op> 0`. The persisted-graph schema
 * makes `rhs` optional for exactly this draft-in-progress case; the save / preview / run boundaries
 * reject an absent threshold so a real routine can never ship without one.
 */
export function conditionToConfig(condition: Condition): ConditionConfig & { lhs: 'row_count'; op: ConditionOp } {
  const rhs = parseFloat(condition.value);
  return {
    lhs: 'row_count',
    op: condition.op,
    ...(Number.isNaN(rhs) ? {} : { rhs }),
  };
}

/** Persisted ConditionConfig → editor condition. An absent `rhs` maps to an empty field (not `'0'`). */
export function configToCondition(cfg: ConditionConfig): Condition {
  return {
    column: cfg.lhs ?? 'row_count',
    op: (cfg.op ?? '>') as ConditionOp,
    value: cfg.rhs === undefined ? '' : String(cfg.rhs),
  };
}

interface AnyNode {
  id: string;
  kind: string;
  config: Record<string, unknown>;
}

/**
 * The draft patch for picking a warehouse in the editor.
 *
 * ACT2-1286: the pick no longer mints the query node's legacy `connectionId` (AI-1370). The node's
 * account link (`accountKey` / `lastAccountId`) is derived by the workflow service on every save from
 * `warehouseId`, and the stored link is kept while the warehouse is unmoved, so the editor carries
 * neither.
 */
export function warehousePickPatch(warehouseId: string): Pick<WorkflowDraft, 'warehouseId'> {
  return { warehouseId };
}

export function graphToDraft(
  name: string,
  description: string,
  trigger: { type: string; cron?: string; timezone?: string },
  graph: { nodes: AnyNode[] },
): WorkflowDraft {
  const queryNode = graph.nodes.find((n) => n.kind === 'query');
  const condNode = graph.nodes.find((n) => n.kind === 'condition');
  const slackNode = graph.nodes.find((n) => n.kind === 'slack');

  const condition: Condition | null = condNode ? configToCondition(condNode.config) : null;

  const qcfg = queryNode?.config as { sql?: string; warehouseId?: string } | undefined;
  const scfg = slackNode?.config as { channel?: string; template?: string } | undefined;

  const warehouseId = qcfg?.warehouseId ?? '';

  return {
    name,
    description,
    schedule: triggerToSchedule(trigger),
    warehouseId,
    sql: qcfg?.sql ?? '',
    condition,
    channel: scfg?.channel ?? '',
    template: scfg?.template ?? '',
  };
}

function generateId(kind: string): string {
  return `${kind}-1`;
}

export function draftToGraph(draft: WorkflowDraft): WorkflowGraph {
  // The terminal destination is always a `slack` node: query → [condition →] slack.
  const queryId = generateId('query');
  const slackId = generateId('slack');

  const nodes: WorkflowGraph['nodes'] = [
    {
      id: queryId,
      kind: 'query',
      config: {
        mode: 'sql',
        sql: draft.sql,
        warehouseId: draft.warehouseId,
      },
    },
  ];

  const edges: WorkflowGraph['edges'] = [];

  if (draft.condition) {
    const condId = generateId('condition');
    nodes.push({
      id: condId,
      kind: 'condition',
      config: { ...conditionToConfig(draft.condition) },
    });
    nodes.push({
      id: slackId,
      kind: 'slack',
      config: { channel: draft.channel, template: draft.template },
    });
    edges.push({ from: queryId, to: condId });
    edges.push({ from: condId, to: slackId, when: 'true' });
  } else {
    nodes.push({
      id: slackId,
      kind: 'slack',
      config: { channel: draft.channel, template: draft.template },
    });
    edges.push({ from: queryId, to: slackId });
  }

  return { nodes, edges };
}
